/**
 * BundleUploadPanel — add files into a matter's SharePoint folder (Phase 4).
 *
 * Drag-and-drop OR file-picker upload into the CURRENT matter. Adds files only;
 * never renames or deletes (the backend forces conflict_behavior=rename). A
 * matter ref is required — without one there is no target folder.
 */
import { useCallback, useRef, useState } from "react";
import { UploadCloud, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { uploadToMatter } from "../../api/bundleApi";

import { errorMessage } from "../../utils/errorMessage";
export default function BundleUploadPanel({ matterRef, onUploaded }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const doUpload = useCallback(async (files) => {
    if (!matterRef) { setError("Select a matter first."); return; }
    if (!files || files.length === 0) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const data = await uploadToMatter(matterRef, files);
      setResult(data);
      if (onUploaded) onUploaded(data);
    } catch (err) {
      setError(errorMessage(err, "Upload failed."));
    } finally {
      setBusy(false);
    }
  }, [matterRef, onUploaded]);

  return (
    <div className="rounded-lg border border-navy-border bg-navy-card p-4" data-testid="bundle-upload-panel">
      <div className="flex items-center gap-2 mb-2">
        <UploadCloud className="text-teal" size={16} />
        <h3 className="text-sm font-semibold text-white">Add files to matter</h3>
      </div>
      <div
        data-testid="bundle-upload-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragOver(false);
          doUpload(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current && inputRef.current.click()}
        className={`cursor-pointer rounded-md border-2 border-dashed px-3 py-6 text-center text-xs transition-colors ${
          dragOver ? "border-teal bg-teal/10" : "border-navy-border hover:border-teal/50"
        }`}
      >
        {busy ? (
          <span className="inline-flex items-center gap-2 text-slate-300">
            <Loader2 className="animate-spin" size={14} /> Uploading…
          </span>
        ) : (
          <span className="text-slate-400">
            Drop files here, or click to choose. Target:{" "}
            <span className="text-teal font-medium">{matterRef || "no matter selected"}</span>
          </span>
        )}
        <input
          ref={inputRef}
          data-testid="bundle-upload-input"
          type="file"
          multiple
          className="hidden"
          onChange={(e) => doUpload(e.target.files)}
        />
      </div>

      {error && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-200" data-testid="bundle-upload-error">
          <AlertTriangle size={13} /> {String(error)}
        </div>
      )}
      {result && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-teal" data-testid="bundle-upload-result">
          <CheckCircle2 size={13} /> Uploaded {result.count} file{result.count === 1 ? "" : "s"} to {result.matter_ref}.
        </div>
      )}
    </div>
  );
}
